<script setup lang="ts">
import { computed, type HTMLAttributes } from "vue"
import { ThemeButton, ThemeImg } from "~/components/theme"
import { useLocation } from "~/composables/locations"
import { cn } from "~/lib/utils"

const { location } = useLocation()

const featuredSection = computed(() => location.value?.pageContent?.featuredSection)

const { 
    class: cl,
    id
} = defineProps<{
    class?: HTMLAttributes["class"]
    id?: string
}>()

const imageOrderClass = "order-1 lg:order-2"
const textOrderClass = "order-2 lg:order-1"
</script>

<template>
    <section
        v-if="featuredSection"
        :id="id"
        :class="cn('space-y-10 md:space-y-12', cl)"
    >
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center p-6 sm:p-8 lg:p-12 rounded-lg bg-featured-section-background text-featured-section-text overflow-hidden">
            <div :class="cn('space-y-6 min-w-0', textOrderClass)">
                <header class="space-y-4">
                    <h2 class="text-2xl sm:text-3xl lg:text-4xl font-headline text-featured-section-text break-words">
                        {{ featuredSection.title }}
                    </h2>
                </header>
                
                <div class="space-y-6">
                    <div class="space-y-4">
                        <p 
                            class="text-base leading-relaxed text-featured-section-text break-words"
                            v-html="featuredSection.content"
                        />
                    </div>
                    <div class="flex flex-col gap-4 sm:flex-row sm:flex-wrap text-center justify-start">
                        <template
                            v-for="button in featuredSection.buttons"
                            :key="button.href"
                        >
                            <a
                                :href="button.href"
                                target="_blank"
                                rel="noopener noreferrer"
                                tabindex="0"
                                :aria-label="button.text"
                                class="w-full sm:w-auto flex justify-center"
                            >
                                <ThemeButton
                                    class="w-full sm:w-auto max-w-full bg-featured-section-button-background hover:bg-featured-section-button-hover-background text-featured-section-button-text hover:text-featured-section-button-hover-text whitespace-normal text-center"
                                >
                                    <span class="break-words">{{ button.text }}</span>
                                </ThemeButton>
                            </a>
                        </template>
                    </div>
                </div>
            </div>

            <div :class="cn(imageOrderClass)">
                <ThemeImg
                    :src="featuredSection.image"
                    :alt="featuredSection.imageAlt || featuredSection.title || ''"
                    class="w-full min-h-64 lg:min-h-80 aspect-[4/3] object-cover rounded-lg shadow-xl"
                />
            </div>
        </div>
    </section>
</template> 